import React, { useState, useMemo, Fragment } from 'react';
import { Outlet, useNavigate, useLocation, useMatch } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { useAppSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { getActiveDudaAccount } from '../../settings/dudaAccounts';
import { DudaNavGroup } from './DudaNavGroup';
import { XeroNavGroup } from '../xero/XeroNavGroup';
import { StripeNavGroup } from '../stripe/StripeNavGroup';
import { WizzardCopilot } from '../wizzard/WizzardCopilot';

const DRAWER_WIDTH = 272;

const SIDEBAR_BG = '#0a0e17';
const SECTION_LABEL = '#5c6b8a';
const ITEM_DEFAULT = '#c4ccd8';
const ITEM_ACTIVE = '#f97316';
const ITEM_ACTIVE_BG = 'rgba(249, 115, 22, 0.14)';

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
};

type NavSection = { title: string; items: NavItem[] };

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [{ path: '/', label: 'Dashboard', icon: <HomeOutlinedIcon sx={{ fontSize: 22 }} /> }],
  },
  {
    title: 'Analytics',
    items: [
      { path: '/reports/income', label: 'Income report', icon: <ShowChartOutlinedIcon sx={{ fontSize: 22, color: '#38bdf8' }} /> },
      { path: '/billing', label: 'Billing', icon: <CreditCardOutlinedIcon sx={{ fontSize: 22, color: '#f472b6' }} /> },
      { path: '/meta', label: 'Facebook & IG', icon: <CampaignOutlinedIcon sx={{ fontSize: 22, color: '#818cf8' }} /> },
      { path: '/integrations/google-ads', label: 'Google Ads', icon: <CampaignOutlinedIcon sx={{ fontSize: 22, color: '#fbbf24' }} /> },
    ],
  },
  {
    title: 'System',
    items: [
      { path: '/sync', label: 'Sync data', icon: <SyncOutlinedIcon sx={{ fontSize: 22, color: '#60a5fa' }} /> },
      { path: '/audit', label: 'Audit', icon: <ManageSearchOutlinedIcon sx={{ fontSize: 22, color: '#94a3b8' }} /> },
      { path: '/settings', label: 'API settings', icon: <SettingsOutlinedIcon sx={{ fontSize: 22, color: '#94a3b8' }} /> },
    ],
  },
];

function buildNavSections(isAdmin: boolean): NavSection[] {
  if (!isAdmin) {
    return NAV_SECTIONS;
  }
  return NAV_SECTIONS.map((section) =>
    section.title === 'System'
      ? {
          ...section,
          items: [
            ...section.items,
            {
              path: '/settings/users',
              label: 'Users',
              icon: <PeopleOutlinedIcon sx={{ fontSize: 22, color: '#e879f9' }} />,
            },
          ],
        }
      : section
  );
}

function pathMatches(pathname: string, itemPath: string): boolean {
  if (itemPath === '/') {
    return pathname === '/';
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

const INTEGRATION_HEADER: Record<string, string> = {
  stripe: 'Stripe',
  xero: 'Xero',
  duda: 'Website (Duda)',
  'google-ads': 'Google Ads',
};

const PAGE_TITLES: { test: (p: string) => boolean; title: string }[] = [
  { test: (p) => p === '/', title: 'Sales console' },
  { test: (p) => p === '/sales', title: 'Sales & analytics' },
  { test: (p) => p === '/reports/income', title: 'Income report' },
  { test: (p) => p === '/billing', title: 'Billing & reminders' },
  { test: (p) => p === '/meta', title: 'Facebook & Instagram' },
  { test: (p) => p === '/duda/analytics-cache', title: 'Duda · analytics' },
  { test: (p) => p.startsWith('/duda/site/'), title: 'Duda · site analytics' },
  { test: (p) => p === '/duda', title: 'Duda' },
  { test: (p) => p.startsWith('/stripe/overview') || p === '/stripe', title: 'Stripe · Overview' },
  { test: (p) => p.startsWith('/stripe/transactions'), title: 'Stripe · Transactions' },
  { test: (p) => p.startsWith('/stripe/customers'), title: 'Stripe · Customers' },
  { test: (p) => p.startsWith('/stripe/subscriptions'), title: 'Stripe · Subscriptions' },
  { test: (p) => p.startsWith('/stripe/payouts'), title: 'Stripe · Payouts' },
  { test: (p) => p.startsWith('/xero/reports') || p === '/xero', title: 'Xero · Reports' },
  { test: (p) => p.startsWith('/xero/invoices'), title: 'Xero · Invoices' },
  { test: (p) => p.startsWith('/xero/customers'), title: 'Xero · Customers' },
  { test: (p) => p === '/sync', title: 'Sync data' },
  { test: (p) => p === '/audit', title: 'Audit' },
  { test: (p) => p === '/settings/users', title: 'Users' },
  { test: (p) => p === '/settings', title: 'API Settings' },
];

const AppShell: React.FC = () => {
  const { settings, updateSettings } = useAppSettings();
  const { enabled: backendAuth, user, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const intMatch = useMatch({ path: '/integrations/:slug', end: true });

  const headerTitle = useMemo(() => {
    if (intMatch?.params.slug) {
      return INTEGRATION_HEADER[intMatch.params.slug] || 'Integration';
    }
    const row = PAGE_TITLES.find((r) => r.test(location.pathname));
    const base = row?.title ?? 'Growth Hub';
    if (location.pathname.startsWith('/duda')) {
      const acc = getActiveDudaAccount(settings);
      return `${base} · ${acc.label}`;
    }
    return base;
  }, [location.pathname, intMatch?.params.slug, settings]);

  const navSections = useMemo(() => buildNavSections(isAdmin), [isAdmin]);

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SIDEBAR_BG,
        color: ITEM_DEFAULT,
      }}
    >
      <Toolbar sx={{ gap: 1, minHeight: 64 }}>
        <HubOutlinedIcon sx={{ color: ITEM_ACTIVE, fontSize: 28 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, color: '#fff' }}>
            iWizz Growth Hub
          </Typography>
          <Typography variant="caption" sx={{ color: SECTION_LABEL, letterSpacing: 0.3 }}>
            Sales & marketing analytics
          </Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(92,107,138,0.25)' }} />

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
        {navSections.map((section) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 2.5,
                py: 1,
                color: SECTION_LABEL,
                fontWeight: 700,
                letterSpacing: '0.12em',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
              }}
            >
              {section.title}
            </Typography>
            <List dense disablePadding sx={{ px: 1 }}>
              {section.items.map((item) => {
                const selected = pathMatches(location.pathname, item.path);
                return (
                  <Fragment key={item.path}>
                    {section.title === 'Analytics' && item.path === '/integrations/google-ads' && (
                      <DudaNavGroup
                        pathname={location.pathname}
                        settings={settings}
                        updateSettings={updateSettings}
                        navigate={(p) => navigate(p)}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    )}
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 1.5,
                        py: 1,
                        mb: 0.25,
                        pl: 2,
                        borderLeft: selected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
                        bgcolor: selected ? ITEM_ACTIVE_BG : 'transparent',
                        color: selected ? ITEM_ACTIVE : ITEM_DEFAULT,
                        '&:hover': {
                          bgcolor: selected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
                        },
                        '&.Mui-selected': {
                          bgcolor: ITEM_ACTIVE_BG,
                          color: ITEM_ACTIVE,
                          '&:hover': { bgcolor: ITEM_ACTIVE_BG },
                        },
                        '& .MuiListItemIcon-root': {
                          color: selected ? ITEM_ACTIVE : 'inherit',
                          minWidth: 40,
                        },
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: selected ? 700 : 500,
                          fontSize: '0.9rem',
                        }}
                      />
                    </ListItemButton>
                    {section.title === 'Analytics' && item.path === '/billing' && (
                      <StripeNavGroup
                        pathname={location.pathname}
                        navigate={(p) => navigate(p)}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    )}
                    {section.title === 'Analytics' && item.path === '/meta' && (
                      <XeroNavGroup
                        pathname={location.pathname}
                        navigate={(p) => navigate(p)}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    )}
                  </Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(92,107,138,0.2)' }}>
        <Button
          size="small"
          variant="outlined"
          fullWidth
          sx={{
            mb: 1.5,
            color: ITEM_DEFAULT,
            borderColor: 'rgba(148,163,184,0.35)',
            '&:hover': {
              borderColor: ITEM_ACTIVE,
              bgcolor: 'rgba(249,115,22,0.08)',
            },
          }}
          onClick={() => window.open('https://www.iwizz.com.au/admin#connections', '_blank')}
        >
          ← Back to iWizz Admin
        </Button>
        <Chip
          size="small"
          label={backendAuth && user ? user.email : `${settings.workspace.name} · local`}
          sx={{
            bgcolor: 'rgba(249,115,22,0.15)',
            color: '#fdba74',
            fontWeight: 700,
            border: '1px solid rgba(249,115,22,0.25)',
            maxWidth: '100%',
          }}
        />
        <Typography variant="caption" display="block" sx={{ mt: 1, color: SECTION_LABEL, lineHeight: 1.4 }}>
          {backendAuth
            ? 'Settings and tokens are stored on the server (SQLite in GCS).'
            : 'Workspace data stays in this browser until you enable the backend.'}
        </Typography>
        {backendAuth && user && (
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1.5, color: ITEM_DEFAULT, borderColor: 'rgba(148,163,184,0.35)' }}
            onClick={() => void logout().then(() => navigate('/login'))}
          >
            Sign out
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: '#fff',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {headerTitle}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ 
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(249, 115, 22, 0.04)',
              },
            }}
            onClick={() => window.open('https://www.iwizz.com.au/admin#connections', '_blank')}
          >
            ← iWizz Admin
          </Button>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: SIDEBAR_BG },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 'none',
              bgcolor: SIDEBAR_BG,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
        <WizzardCopilot />
      </Box>
    </Box>
  );
};

export default AppShell;
